import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { RoleEnum } from "@/types/auth/roles";
import { hasTraderFullAccess } from "@/auth/traderAccess";
import {
  dictionariesService,
  inviteTokensService,
  InviteTargetRole,
  InviteToken,
  tradersService,
} from "@/services";

interface Currency {
  id: string;
  code?: string;
  iso_code?: string;
}

interface TraderGroup {
  id: string;
  name: string;
}

const traderRegistrationUrl =
  import.meta.env.VITE_TRADER_REGISTRATION_URL ||
  "https://trader.sprutprocessing.online/registration";
const merchantRegistrationUrl =
  import.meta.env.VITE_MERCHANT_REGISTRATION_URL ||
  "https://merchant.sprutprocessing.online";

const registrationLink = (item: InviteToken) => {
  const baseUrl =
    item.target_role === "TRADER"
      ? traderRegistrationUrl
      : merchantRegistrationUrl;
  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}invite_token=${encodeURIComponent(item.token)}`;
};

const registeredEntityId = (item: InviteToken) =>
  item.target_role === "TRADER"
    ? item.invited_trader_id
    : item.invited_merchant_id;

const inviteDate = (value: string) => new Date(/Z$|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`);

export default function InviteTokens() {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isClientManager = currentUser?.role === RoleEnum.CLIENT_MANAGER;
  const isMerchantOnlyClientManager =
    isClientManager && !hasTraderFullAccess(currentUser);
  const [items, setItems] = useState<InviteToken[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [traderGroups, setTraderGroups] = useState<TraderGroup[]>([]);
  const [role, setRole] = useState<InviteTargetRole>("TRADER");
  const [currencyId, setCurrencyId] = useState("");
  const [traderGroupId, setTraderGroupId] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [listRole, setListRole] = useState("");
  const [listStatus, setListStatus] = useState("active");
  const effectiveRole: InviteTargetRole = isMerchantOnlyClientManager
    ? "MERCHANT"
    : role;
  const filteredItems = items.filter((item) => {
    const expired = Boolean(
      item.expires_at && inviteDate(item.expires_at) <= new Date(),
    );
    const status = item.used
      ? "used"
      : !item.is_active
        ? "disabled"
        : expired
          ? "expired"
          : "active";
    const needle = search.trim().toLowerCase();
    const matchesSearch =
      !needle ||
      [
        item.token,
        item.currency_code,
        item.trader_group_name,
        item.id,
        item.invited_username,
        item.invited_entity_name,
        item.invited_user_id,
        registeredEntityId(item),
      ].some((value) => value?.toLowerCase().includes(needle));

    return (
      matchesSearch &&
      (!listRole || item.target_role === listRole) &&
      (!listStatus || status === listStatus)
    );
  });

  const load = useCallback(async () => {
    try {
      const groupListPromise: Promise<TraderGroup[]> = isMerchantOnlyClientManager
        ? Promise.resolve([])
        : tradersService.getTraderGroups();
      const [tokens, currencyList, groupList] = await Promise.all([
        inviteTokensService.list(),
        dictionariesService.getCurrencies(),
        groupListPromise,
      ]);
      setItems(
        isMerchantOnlyClientManager
          ? tokens.filter((item) => item.target_role === "MERCHANT")
          : tokens,
      );
      setCurrencies(currencyList);
      setTraderGroups(groupList);
    } catch {
      setError("Не удалось загрузить invite-токены");
    }
  }, [isMerchantOnlyClientManager]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    try {
      setError("");
      await inviteTokensService.create({
        target_role: effectiveRole,
        ...(currencyId && { currency_id: currencyId }),
        ...(effectiveRole === "TRADER" &&
          traderGroupId && { trader_group_id: traderGroupId }),
        ...(expiresAt && { expires_at: new Date(expiresAt).toISOString() }),
      });
      await load();
    } catch {
      setError("Не удалось создать invite-токен");
    }
  };

  const copy = (item: InviteToken) => {
    void navigator.clipboard.writeText(registrationLink(item));
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Invite-токены
      </Typography>
      <Paper sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Тип пользователя</InputLabel>
            <Select
              value={effectiveRole}
              label="Тип пользователя"
              disabled={isMerchantOnlyClientManager}
              onChange={(e) => {
                const nextRole = e.target.value as InviteTargetRole;
                setRole(nextRole);
                if (nextRole !== "TRADER") {
                  setTraderGroupId("");
                }
              }}
            >
              {!isMerchantOnlyClientManager && <MenuItem value="TRADER">Трейдер</MenuItem>}
              <MenuItem value="MERCHANT">Мерчант</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel>GEO / валюта</InputLabel>
            <Select
              value={currencyId}
              label="GEO / валюта"
              onChange={(e) => setCurrencyId(e.target.value)}
            >
              <MenuItem value="">Любая</MenuItem>
              {currencies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.code || c.iso_code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {effectiveRole === "TRADER" && (
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel>Тимлид / группа</InputLabel>
              <Select
                value={traderGroupId}
                label="Тимлид / группа"
                onChange={(e) => setTraderGroupId(e.target.value)}
              >
                <MenuItem value="">Без группы</MenuItem>
                {traderGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Действует до"
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" onClick={create}>
            Создать токен
          </Button>
        </Box>
      {effectiveRole === "MERCHANT" && (
          <Alert severity="info" sx={{ mb: 2 }}>
            После создания токена скопируйте ссылку регистрации мерчанта:{" "}
            {merchantRegistrationUrl}
          </Alert>
      )}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <TextField
          sx={{ flex: 1, minWidth: 280 }}
          label="Поиск по токену, пользователю, ID или GEO"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {!isMerchantOnlyClientManager && (
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Тип</InputLabel>
            <Select
              value={listRole}
              label="Тип"
              onChange={(event) => setListRole(event.target.value)}
            >
              <MenuItem value="">Все</MenuItem>
              <MenuItem value="TRADER">Трейдер</MenuItem>
              <MenuItem value="MERCHANT">Мерчант</MenuItem>
            </Select>
          </FormControl>
        )}
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Статус</InputLabel>
          <Select
            value={listStatus}
            label="Статус"
            onChange={(event) => setListStatus(event.target.value)}
          >
            <MenuItem value="">Все</MenuItem>
            <MenuItem value="active">Активные</MenuItem>
            <MenuItem value="used">Использованные</MenuItem>
            <MenuItem value="expired">Истёкшие</MenuItem>
            <MenuItem value="disabled">Отключённые</MenuItem>
          </Select>
        </FormControl>
        <Button
          onClick={() => {
            setSearch("");
            setListRole("");
            setListStatus("");
          }}
        >
          Сбросить
        </Button>
      </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Токен</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell>Зарегистрирован</TableCell>
              <TableCell>GEO</TableCell>
              <TableCell>Тимлид / группа</TableCell>
              <TableCell>Создан</TableCell>
              <TableCell>Дата регистрации</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.map((item) => {
              const entityId = registeredEntityId(item);
              const expired =
                item.expires_at && inviteDate(item.expires_at) <= new Date();
              const status = item.used
                ? "Использован"
                : !item.is_active
                  ? "Отключён"
                  : expired
                    ? "Истёк"
                    : "Активен";
              return (
                <TableRow key={item.id}>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {item.token}
                  </TableCell>
                  <TableCell>{item.target_role}</TableCell>
                  <TableCell>
                    {item.used ? (
                      <Box sx={{ minWidth: 220 }}>
                        <Typography variant="body2">
                          {item.invited_entity_name ||
                            item.invited_username ||
                            "Данные удалены"}
                        </Typography>
                        {item.invited_username &&
                          item.invited_username !== item.invited_entity_name && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Логин: {item.invited_username}
                            </Typography>
                          )}
                        {entityId && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ fontFamily: "monospace" }}
                          >
                            ID: {entityId}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>{item.currency_code || "Любая"}</TableCell>
                  <TableCell>{item.trader_group_name || "—"}</TableCell>
                  <TableCell>
                    {inviteDate(item.created_at).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' })}
                  </TableCell>
                  <TableCell>{item.used_at ? inviteDate(item.used_at).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' }) : '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={status}
                      color={status === "Активен" ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => copy(item)}
                      disabled={item.used || !item.is_active || Boolean(expired)}
                    >
                      Копировать
                    </Button>
                    {!item.used && (!item.is_active || expired) && <Button size="small" color="error" onClick={async () => {
                      if (!window.confirm('Удалить неиспользованную отключённую или истёкшую ссылку?')) return;
                      try { await inviteTokensService.remove(item.id); await load(); }
                      catch { setError('Не удалось удалить ссылку'); }
                    }}>Удалить</Button>}
                    {!item.used && item.is_active && !expired && (
                      <Button
                        size="small"
                        color="error"
                        onClick={async () => {
                          await inviteTokensService.deactivate(item.id);
                          await load();
                        }}
                      >
                        Отключить
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
